import React, { useEffect, useRef, useState } from "react";
import { loadGoogleMaps, IBIZA_BOUNDS, IBIZA_CENTER, kmToZone } from "./lib/googleMaps";

/**
 * Custom address autocomplete — uses the new `AutocompleteSuggestion`
 * API + a hand-rendered dropdown so the list always appears flush below
 * the input (no weird positioning on mobile with the default
 * `gmp-place-autocomplete` custom element).
 */
function AddressAutocomplete({ value, onChange, onSelectPlace, placeholder, label, google, bounds, testid }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);

  useEffect(() => {
    if (!google) return;
    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
  }, [google]);

  const fetchSuggestions = async (input) => {
    if (!google || !input || input.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const request = {
        input,
        sessionToken: sessionTokenRef.current,
        includedRegionCodes: ["es"],
        // Strict Ibiza-only restriction — hardcoded so it applies even before
        // the `bounds` state has finished loading.
        locationRestriction: {
          north: IBIZA_BOUNDS.north,
          south: IBIZA_BOUNDS.south,
          east: IBIZA_BOUNDS.east,
          west: IBIZA_BOUNDS.west,
        },
      };
      const res = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      setSuggestions(res?.suggestions || []);
    } catch (e) { console.warn("autocomplete err", e); setSuggestions([]); }
  };

  const onInputChange = (e) => {
    const v = e.target.value;
    onChange(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 180);
    setOpen(true);
  };

  const handleSelect = async (s) => {
    const pred = s.placePrediction;
    if (!pred) return;
    try {
      const place = pred.toPlace();
      await place.fetchFields({ fields: ["formattedAddress", "location", "displayName"] });
      // Prefer the full prediction text the user saw in the dropdown — it
      // always includes the place name (e.g. "Aeropuerto de Ibiza, ...").
      // Fall back to displayName + formattedAddress, then formattedAddress
      // alone, then anything else.
      const fullText = pred.text?.text;
      const name = place.displayName;
      const formatted = place.formattedAddress;
      let addr = "";
      if (fullText) {
        addr = fullText;
      } else if (name && formatted && !formatted.toLowerCase().includes(name.toLowerCase())) {
        addr = `${name}, ${formatted}`;
      } else {
        addr = formatted || name || "";
      }
      onChange(addr);
      let lat = null, lng = null;
      if (place.location) {
        lat = typeof place.location.lat === "function" ? place.location.lat() : place.location.lat;
        lng = typeof place.location.lng === "function" ? place.location.lng() : place.location.lng;
      }
      onSelectPlace({ address: addr, lat, lng });
      setSuggestions([]);
      setOpen(false);
      // New session after each selection (Places API billing best-practice)
      if (google) sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    } catch (e) { console.warn("place details err", e); }
  };

  const mainText = (s) =>
    s?.placePrediction?.structuredFormat?.mainText?.text ||
    s?.placePrediction?.text?.text || "";
  const secondaryText = (s) =>
    s?.placePrediction?.structuredFormat?.secondaryText?.text || "";

  return (
    <div className="relative">
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-2">
        {label} *
      </label>
      <input
        data-testid={testid}
        type="text"
        value={value}
        onChange={onInputChange}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Small delay so the mousedown on a suggestion fires before closing
          blurTimerRef.current = setTimeout(() => setOpen(false), 180);
        }}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full bg-transparent border-b border-[#C9A961]/30 focus:border-[#C9A961] text-white py-2 outline-none transition-colors placeholder:text-white/30"
      />

      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testid}-list`}
          className="absolute z-40 left-0 right-0 top-full mt-1 bg-[#0D1424] border border-[#C9A961]/30 max-h-72 overflow-auto shadow-[0_20px_40px_rgba(0,0,0,0.5)]"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.placePrediction?.placeId || i}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
              onTouchStart={(e) => { e.preventDefault(); handleSelect(s); }}
              className="px-4 py-3 cursor-pointer hover:bg-[#C9A961]/10 border-b border-[#C9A961]/10 last:border-b-0"
            >
              <p className="text-sm text-white truncate">{mainText(s)}</p>
              {secondaryText(s) && (
                <p className="text-[11px] text-white/50 truncate mt-0.5">{secondaryText(s)}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function RouteMap({
  origin,
  destination,
  zones,
  onOriginChange,
  onDestinationChange,
  onRouteChange,
  lang = "es",
}) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const googleRef = useRef(null);
  const initDoneRef = useRef(false);

  const originPlaceRef = useRef(null);
  const destinationPlaceRef = useRef(null);
  const [selectionTick, setSelectionTick] = useState(0);
  const [google, setGoogle] = useState(null);
  const [bounds, setBounds] = useState(null);

  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [outOfArea, setOutOfArea] = useState(false);

  const isInIbiza = (latLng) => {
    const lat = typeof latLng?.lat === "function" ? latLng.lat() : latLng?.lat;
    const lng = typeof latLng?.lng === "function" ? latLng.lng() : latLng?.lng;
    if (lat == null || lng == null) return false;
    return (
      lat >= IBIZA_BOUNDS.south && lat <= IBIZA_BOUNDS.north &&
      lng >= IBIZA_BOUNDS.west  && lng <= IBIZA_BOUNDS.east
    );
  };

  const labels = lang === "es"
    ? {
        loading: "Cargando mapa...",
        errorKey: "Falta o es inválida la API Key de Google Maps.",
        errorGeneric: "No se pudo cargar el mapa.",
        distance: "Distancia",
        duration: "Duración",
        noRoute: "Introduce un origen y un destino para ver la ruta.",
        originLabel: "Origen",
        destinationLabel: "Destino",
        originPh: "Escribe una dirección en Ibiza",
        destinationPh: "Escribe una dirección en Ibiza",
      }
    : {
        loading: "Loading map...",
        errorKey: "Missing or invalid Google Maps API key.",
        errorGeneric: "Could not load the map.",
        distance: "Distance",
        duration: "Duration",
        noRoute: "Enter pickup and drop-off to see the route.",
        originLabel: "Pickup",
        destinationLabel: "Drop-off",
        originPh: "Type an address in Ibiza",
        destinationPh: "Type an address in Ibiza",
      };

  useEffect(() => {
    if (initDoneRef.current) return;
    let cancelled = false;
    setStatus("loading");

    loadGoogleMaps()
      .then((g) => {
        if (cancelled) return;
        googleRef.current = g;
        setGoogle(g);

        const map = new g.maps.Map(mapEl.current, {
          center: IBIZA_CENTER,
          zoom: 11,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "cooperative",
          styles: lightMapStyle,
        });
        mapRef.current = map;

        directionsServiceRef.current = new g.maps.DirectionsService();
        directionsRendererRef.current = new g.maps.DirectionsRenderer({
          map,
          suppressMarkers: false,
          polylineOptions: {
            strokeColor: "#C9A961",
            strokeWeight: 5,
            strokeOpacity: 0.95,
          },
        });

        const b = new g.maps.LatLngBounds(
          { lat: IBIZA_BOUNDS.south, lng: IBIZA_BOUNDS.west },
          { lat: IBIZA_BOUNDS.north, lng: IBIZA_BOUNDS.east }
        );
        setBounds(b);

        initDoneRef.current = true;
        setStatus("ready");
      })
      .catch((e) => {
        console.error("Google Maps load error:", e);
        if (cancelled) return;
        const msg = String(e?.message || e);
        setError(msg.includes("InvalidKey") || msg.includes("ApiNotActivated") ? labels.errorKey : labels.errorGeneric);
        setStatus("error");
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== "ready" && status !== "routing") return;
    if (!origin || !destination) {
      setRouteInfo(null);
      if (directionsRendererRef.current) directionsRendererRef.current.setDirections({ routes: [] });
      return;
    }

    const oPlace = originPlaceRef.current;
    const dPlace = destinationPlaceRef.current;
    if (oPlace && oPlace.address && origin !== oPlace.address) originPlaceRef.current = null;
    if (dPlace && dPlace.address && destination !== dPlace.address) destinationPlaceRef.current = null;

    const originArg = originPlaceRef.current
      ? { lat: originPlaceRef.current.lat, lng: originPlaceRef.current.lng }
      : origin;
    const destArg = destinationPlaceRef.current
      ? { lat: destinationPlaceRef.current.lat, lng: destinationPlaceRef.current.lng }
      : destination;

    setStatus("routing");
    directionsServiceRef.current.route(
      {
        origin: originArg,
        destination: destArg,
        travelMode: googleRef.current.maps.TravelMode.DRIVING,
        region: "es",
      },
      (result, dsStatus) => {
        setStatus("ready");
        if (dsStatus !== "OK" || !result) {
          console.warn("Directions error:", dsStatus);
          setRouteInfo(null);
          setOutOfArea(false);
          return;
        }

        // Enforce Ibiza-only service: reject any route whose endpoints fall
        // outside the island bounding box.
        const leg = result.routes[0].legs[0];
        if (!isInIbiza(leg.start_location) || !isInIbiza(leg.end_location)) {
          directionsRendererRef.current.setDirections({ routes: [] });
          setRouteInfo(null);
          setOutOfArea(true);
          if (typeof onRouteChange === "function") onRouteChange(null);
          return;
        }
        setOutOfArea(false);

        directionsRendererRef.current.setDirections(result);

        const distanceKm = leg.distance.value / 1000;
        const durationMin = Math.round(leg.duration.value / 60);
        const zone = kmToZone(distanceKm, zones);

        const info = {
          distanceKm: Math.round(distanceKm * 10) / 10,
          durationMin,
          zone: zone.id,
          price: zone.price,
          originLabel: leg.start_address,
          destinationLabel: leg.end_address,
        };
        setRouteInfo(info);
        if (typeof onRouteChange === "function") onRouteChange(info);
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, destination, selectionTick]);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AddressAutocomplete
          label={labels.originLabel}
          placeholder={labels.originPh}
          value={origin}
          onChange={onOriginChange}
          onSelectPlace={(p) => { originPlaceRef.current = p; setSelectionTick((n) => n + 1); }}
          google={google}
          bounds={bounds}
          testid="map-origin-input"
        />
        <AddressAutocomplete
          label={labels.destinationLabel}
          placeholder={labels.destinationPh}
          value={destination}
          onChange={onDestinationChange}
          onSelectPlace={(p) => { destinationPlaceRef.current = p; setSelectionTick((n) => n + 1); }}
          google={google}
          bounds={bounds}
          testid="map-destination-input"
        />
      </div>

      <div className="relative">
        <div
          ref={mapEl}
          data-testid="route-map"
          className="w-full h-[320px] md:h-[400px] border border-[#C9A961]/20 bg-[#0D1424]"
        />
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center text-[#C9A961]/70 text-sm uppercase tracking-[0.25em] pointer-events-none">
            {labels.loading}
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm px-6 text-center bg-[#0D1424]/80">
            {error}
          </div>
        )}
      </div>

      {routeInfo && (
        <div
          data-testid="route-info"
          className="grid grid-cols-3 border border-[#C9A961]/30 bg-[#0D1424]/80 backdrop-blur-sm"
        >
          <div className="p-4 border-r border-[#C9A961]/15">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">{labels.distance}</div>
            <div className="font-[Playfair_Display] text-2xl mt-1">{routeInfo.distanceKm} km</div>
          </div>
          <div className="p-4 border-r border-[#C9A961]/15">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">{labels.duration}</div>
            <div className="font-[Playfair_Display] text-2xl mt-1">{routeInfo.durationMin} min</div>
          </div>
          <div className="p-4 bg-[#C9A961]/10">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">Zona {routeInfo.zone}</div>
            <div className="font-[Playfair_Display] text-2xl mt-1 text-[#C9A961]">{routeInfo.price}€</div>
          </div>
        </div>
      )}

      {!routeInfo && status === "ready" && !outOfArea && (
        <p className="text-xs text-white/50 italic">{labels.noRoute}</p>
      )}

      {outOfArea && (
        <p data-testid="out-of-area-warning" className="text-sm border border-red-500/40 bg-red-500/10 text-red-300 p-4">
          {lang === "es"
            ? "⚠️ IBIALCON solo ofrece servicio dentro de la isla de Ibiza. Selecciona una dirección que esté en Ibiza."
            : "⚠️ IBIALCON only operates within Ibiza island. Please choose an address within Ibiza."}
        </p>
      )}
    </div>
  );
}

// Warm Mediterranean light map style
const lightMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#F3E7CB" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#3E3A2A" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#F3E7CB" }, { weight: 3 }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#b38f4a" }, { weight: 0.4 }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#8B6A2E" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#8B6A2E" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#C7D7A1" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#4e6b2c" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#EEDEB0" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#FFFFFF" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#d8c38a" }, { weight: 0.5 }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#6a5a30" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#FFF4D5" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#F5CD6A" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#b38f4a" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#dcc9a0" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#5AB1CC" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#FFFFFF" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#2E6B80" }, { weight: 2 }] },
];
